import { useState } from "react";

const photoLocations = [
  { name: "Walgreens — Thornton", address: "700 W 84th Ave, Thornton, CO 80260", hours: "Daily 7 AM – 10 PM" },
  { name: "CVS Pharmacy — Thornton", address: "3795 E 120th Ave, Thornton, CO 80233", hours: "Daily 7 AM – 10 PM" },
  { name: "Walgreens — Northglenn", address: "10795 Huron St, Northglenn, CO 80234", hours: "Daily 7 AM – 10 PM" },
  { name: "The UPS Store — Thornton", address: "9225 Dorchester St, Thornton, CO 80229", hours: "Call for hours" },
  { name: "Walgreens — Brighton", address: "775 E Bridge St, Brighton, CO 80601", hours: "Daily 7 AM – 10 PM" },
  { name: "FedEx Office — Westminster", address: "3455 W 92nd Ave, Westminster, CO 80031", hours: "Call for hours" },
];

const dmvLocations = [
  {
    name: "Colorado DMV — Brighton",
    address: "25 S 4th Ave, Brighton, CO 80601",
    hours: "Mon–Fri, 8:00 AM – 4:30 PM",
    note: "Driver licenses, ID cards, and REAL ID. Serves Adams County including Thornton. Appointment strongly recommended.",
    featured: true,
  },
  {
    name: "Colorado DMV — Westminster",
    address: "8799 Wadsworth Blvd, Arvada, CO 80003",
    hours: "Mon–Fri, 8:00 AM – 4:30 PM",
    note: "Convenient for south Thornton and Westminster residents. Call ahead for current wait times.",
    featured: false,
  },
];

export default function HelpfulResourcesSection() {
  const [photoExpanded, setPhotoExpanded] = useState(false);
  const visiblePhotos = photoExpanded ? photoLocations : photoLocations.slice(0, 4);

  return (
    <section id="local-resources" className="bg-[#F7F8FA] py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Local Resources</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">
            Helpful Resources for Thornton Travelers
          </h2>
          <p className="text-gray-500 max-w-2xl mx-auto text-base leading-relaxed">
            Getting your passport ready involves more than just the application. Here are local north Denver metro resources to help you gather everything you need before your next international trip.
          </p>
        </div>

        <div className="space-y-6">
          {/* Passport Photos */}
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <div className="bg-[#1A365D] px-6 py-5 flex items-center gap-3">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-white/15 flex-shrink-0">
                <i className="ri-camera-line text-white text-lg"></i>
              </div>
              <div>
                <p className="text-white/60 text-xs font-semibold uppercase tracking-widest">Passport Photos</p>
                <p className="text-white font-bold text-base leading-tight mt-0.5">Passport Photo Locations Near Thornton</p>
              </div>
            </div>
            <div className="px-6 py-6">
              <p className="text-gray-500 text-sm leading-relaxed mb-4">
                Your passport application requires two identical 2&quot;&times;2&quot; passport photos taken within the last 6 months. Popular passport photo locations near Thornton include:
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-4">
                {visiblePhotos.map((loc, i) => (
                  <div key={i} className="bg-[#F7F8FA] border border-gray-200 rounded-lg p-3.5">
                    <p className="font-bold text-[#1A365D] text-xs leading-snug mb-1">{loc.name}</p>
                    <p className="text-gray-500 text-xs leading-relaxed">{loc.address}</p>
                    <p className="text-gray-400 text-xs mt-1">{loc.hours}</p>
                  </div>
                ))}
              </div>
              {!photoExpanded && (
                <button
                  onClick={() => setPhotoExpanded(true)}
                  className="text-[#1A365D] hover:text-[#F6C344] font-semibold text-xs flex items-center gap-1.5 mb-4 cursor-pointer transition-colors"
                >
                  <i className="ri-add-circle-line text-sm"></i>
                  Show all {photoLocations.length} photo locations
                </button>
              )}
              <div className="bg-[#F7F8FA] border border-gray-200 rounded-md p-3">
                <p className="text-gray-600 text-xs leading-relaxed">
                  <i className="ri-lightbulb-line text-[#F6C344] mr-1.5"></i>
                  <strong>Tip:</strong> Wear a plain white or off-white top, no glasses, neutral expression. Photos must meet strict U.S. State Department specifications — ask the technician to confirm compliance before you leave.
                </p>
              </div>
            </div>
          </div>

          {/* DMV */}
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <div className="bg-[#1A365D] px-6 py-5 flex items-center gap-3">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-white/15 flex-shrink-0">
                <i className="ri-id-card-line text-white text-lg"></i>
              </div>
              <div>
                <p className="text-white/60 text-xs font-semibold uppercase tracking-widest">Driver&apos;s License &amp; State ID</p>
                <p className="text-white font-bold text-base leading-tight mt-0.5">Colorado DMV Services — Thornton Area</p>
              </div>
            </div>
            <div className="px-6 py-6">
              <p className="text-gray-500 text-sm leading-relaxed mb-5">
                A valid government-issued photo ID is required for your passport application. If you need a new or renewed Colorado driver&apos;s license or REAL ID, the Colorado DMV has offices serving the Thornton and north metro area.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-5">
                {dmvLocations.map((loc, i) => (
                  <div key={i} className={`rounded-lg p-4 border ${loc.featured ? "bg-amber-50 border-[#F6C344]/40" : "bg-[#F7F8FA] border-gray-200"}`}>
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <i className="ri-map-pin-2-line text-[#1A365D] text-base"></i>
                      </div>
                      <div>
                        <p className="font-bold text-[#1A365D] text-xs leading-snug">{loc.name}</p>
                        <p className="text-gray-500 text-xs mt-1">{loc.address}</p>
                        <p className="text-gray-400 text-xs mt-0.5">{loc.hours}</p>
                        <p className="text-[#1A365D] text-xs font-medium mt-1.5 leading-snug">{loc.note}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <div className="bg-amber-50 border border-amber-200 rounded-md p-3">
                <p className="text-gray-600 text-xs leading-relaxed">
                  <i className="ri-calendar-check-line text-amber-500 mr-1.5"></i>
                  <strong>Appointment recommended</strong> — walk-in wait times can be long. Book your slot online at the Colorado DMV website before visiting.
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <div className="bg-[#1A365D] px-6 py-5 flex items-center gap-3">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-white/15 flex-shrink-0">
                <i className="ri-file-text-line text-white text-lg"></i>
              </div>
              <div>
                <p className="text-white/60 text-xs font-semibold uppercase tracking-widest">Vital Records</p>
                <p className="text-white font-bold text-base leading-tight mt-0.5">Birth, Death, Marriage &amp; Divorce Records — Thornton</p>
              </div>
            </div>
            <div className="px-6 py-6">
              <p className="text-gray-500 text-sm leading-relaxed mb-5">If you need a certified copy of your birth certificate for a passport application, here are the official sources for Thornton (Adams County) residents.</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-5">
                <a href="https://cdphe.colorado.gov/vital-records" target="_blank" rel="noopener noreferrer" className="flex items-start gap-3.5 bg-[#F7F8FA] border border-gray-200 rounded-lg p-4 hover:border-[#F6C344] transition-all group">
                  <div className="w-9 h-9 flex items-center justify-center rounded-full bg-[#EEF2F8] flex-shrink-0 mt-0.5"><i className="ri-user-heart-line text-[#1A365D] text-base group-hover:text-[#F6C344] transition-colors"></i></div>
                  <div className="flex-1 min-w-0"><p className="font-bold text-[#1A365D] text-xs leading-snug mb-1">Birth Certificates</p><p className="text-gray-500 text-xs leading-relaxed mb-1.5">Colorado Department of Public Health and Environment — Vital Records. Order online, by mail, or at the Adams County office.</p><span className="inline-flex items-center gap-1 text-[#1A365D] font-semibold text-xs group-hover:text-[#F6C344] transition-colors"><i className="ri-external-link-line text-xs"></i>Get Records</span></div>
                </a>
                <a href="https://cdphe.colorado.gov/vital-records" target="_blank" rel="noopener noreferrer" className="flex items-start gap-3.5 bg-[#F7F8FA] border border-gray-200 rounded-lg p-4 hover:border-[#F6C344] transition-all group">
                  <div className="w-9 h-9 flex items-center justify-center rounded-full bg-[#EEF2F8] flex-shrink-0 mt-0.5"><i className="ri-heart-pulse-line text-[#1A365D] text-base group-hover:text-[#F6C344] transition-colors"></i></div>
                  <div className="flex-1 min-w-0"><p className="font-bold text-[#1A365D] text-xs leading-snug mb-1">Death Certificates</p><p className="text-gray-500 text-xs leading-relaxed mb-1.5">Certified copies available through Colorado Vital Records.</p><span className="inline-flex items-center gap-1 text-[#1A365D] font-semibold text-xs group-hover:text-[#F6C344] transition-colors"><i className="ri-external-link-line text-xs"></i>Get Records</span></div>
                </a>
                <a href="https://cdphe.colorado.gov/vital-records" target="_blank" rel="noopener noreferrer" className="flex items-start gap-3.5 bg-[#F7F8FA] border border-gray-200 rounded-lg p-4 hover:border-[#F6C344] transition-all group">
                  <div className="w-9 h-9 flex items-center justify-center rounded-full bg-[#EEF2F8] flex-shrink-0 mt-0.5"><i className="ri-links-line text-[#1A365D] text-base group-hover:text-[#F6C344] transition-colors"></i></div>
                  <div className="flex-1 min-w-0"><p className="font-bold text-[#1A365D] text-xs leading-snug mb-1">Marriage Certificates</p><p className="text-gray-500 text-xs leading-relaxed mb-1.5">Adams County Clerk and Recorder or Colorado Vital Records.</p><span className="inline-flex items-center gap-1 text-[#1A365D] font-semibold text-xs group-hover:text-[#F6C344] transition-colors"><i className="ri-external-link-line text-xs"></i>Get Records</span></div>
                </a>
                <a href="https://www.courts.state.co.us/" target="_blank" rel="noopener noreferrer" className="flex items-start gap-3.5 bg-[#F7F8FA] border border-gray-200 rounded-lg p-4 hover:border-[#F6C344] transition-all group">
                  <div className="w-9 h-9 flex items-center justify-center rounded-full bg-[#EEF2F8] flex-shrink-0 mt-0.5"><i className="ri-scales-3-line text-[#1A365D] text-base group-hover:text-[#F6C344] transition-colors"></i></div>
                  <div className="flex-1 min-w-0"><p className="font-bold text-[#1A365D] text-xs leading-snug mb-1">Divorce Decrees</p><p className="text-gray-500 text-xs leading-relaxed mb-1.5">Adams County District Court. Contact the Clerk for certified copies.</p><span className="inline-flex items-center gap-1 text-[#1A365D] font-semibold text-xs group-hover:text-[#F6C344] transition-colors"><i className="ri-external-link-line text-xs"></i>Get Records</span></div>
                </a>
              </div>
              <div className="bg-amber-50 border border-amber-200 rounded-md p-3"><p className="text-gray-600 text-xs leading-relaxed"><i className="ri-information-line text-amber-500 mr-1.5"></i><strong>Don&apos;t see your county?</strong> Visit <a href="https://www.vitalchek.com/" target="_blank" rel="noopener noreferrer" className="font-bold text-[#1A365D] underline underline-offset-2 hover:text-[#F6C344] transition-colors">VitalChek.com</a>.</p></div>
            </div>
          </div>

          {/* Airport */}
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <div className="bg-[#1A365D] px-6 py-5 flex items-center gap-3">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-white/15 flex-shrink-0">
                <i className="ri-flight-takeoff-line text-white text-lg"></i>
              </div>
              <div>
                <p className="text-white/60 text-xs font-semibold uppercase tracking-widest">Airport</p>
                <p className="text-white font-bold text-base leading-tight mt-0.5">Denver International Airport (DIA) — Thornton&apos;s Gateway</p>
              </div>
            </div>
            <div className="px-6 py-6">
              <p className="text-gray-500 text-sm leading-relaxed mb-5">
                Denver International Airport (DEN) is just 20 minutes from Thornton via E-470, making it one of the most accessible major airports from any north Denver suburb. DIA offers nonstop service to Europe, Asia, Mexico, Central America, Canada, and major domestic hubs — making it the primary departure point for Thornton residents traveling internationally.
              </p>
              <div className="bg-amber-50 border border-amber-200 rounded-md p-3">
                <p className="text-gray-600 text-xs leading-relaxed">
                  <i className="ri-passport-line text-amber-500 mr-1.5"></i>
                  <strong>Passport required for all international flights.</strong> If your DIA departure is within two weeks, standard processing won&apos;t make it in time — <a href="https://fastpassportcenter.com/checkout/" className="font-bold text-[#1A365D] underline underline-offset-2 hover:text-[#F6C344] transition-colors">expedited processing through Fast Passport Center</a> is your most reliable option.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}